import React, { useEffect, useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { useAppContext } from '../context/AppContext';
import { db } from '../utils/db';
import {
    LEAK_BUCKETS,
    budgetCategoryKey,
    getLeakBucketSpending,
    formatINR,
} from '../utils/leakBuckets';

/**
 * Minimalist 3-bucket leak target setters.
 */
export default function LeakTargetsView({ onContinue }) {
    const { allTransactions, isSampleMode, sampleBudgets, triggerSampleLocked } = useAppContext();

    const dbBudgets = useLiveQuery(() => db.budgets.toArray(), []) || [];
    const existing = isSampleMode ? sampleBudgets : dbBudgets;

    const [limits, setLimits] = useState(() => {
        const init = {};
        for (const b of LEAK_BUCKETS) {
            const key = budgetCategoryKey(b.id);
            const found = (existing || []).find(x => x.category === key || x.category === b.label);
            init[b.id] = found?.limit ?? b.defaultLimit;
        }
        return init;
    });

    useEffect(() => {
        if (!existing?.length) return;
        setLimits(prev => {
            const next = { ...prev };
            for (const b of LEAK_BUCKETS) {
                const key = budgetCategoryKey(b.id);
                const found = existing.find(x => x.category === key || x.category === b.label);
                if (found?.limit != null) next[b.id] = found.limit;
            }
            return next;
        });
    }, [existing]);

    const spent = useMemo(() => getLeakBucketSpending(allTransactions), [allTransactions]);

    const save = async () => {
        if (isSampleMode) {
            triggerSampleLocked();
            return;
        }
        for (const b of LEAK_BUCKETS) {
            await db.budgets.put({
                category: budgetCategoryKey(b.id),
                limit: Number(limits[b.id]) || 0,
                label: b.label,
            });
        }
        localStorage.setItem('spenderly_leak_targets_set', '1');
        onContinue?.();
    };

    return (
        <div className="leak-flow-view">
            <p className="leak-eyebrow">Leak limits</p>
            <h1 className="leak-display">Set your caps</h1>
            <p className="leak-sub">Dining · quick commerce · cabs</p>

            <div className="leak-sliders">
                {LEAK_BUCKETS.map(b => (
                    <div key={b.id} className="leak-slider-row">
                        <div className="leak-slider-head">
                            <div>
                                <div className="leak-slider-title">{b.shortLabel || b.label}</div>
                                <div className="leak-slider-desc">
                                    {b.description}
                                    {spent[b.id]?.spent > 0 && <> · {formatINR(spent[b.id].spent)} last</>}
                                </div>
                            </div>
                            <div className="leak-slider-value">{formatINR(limits[b.id])}</div>
                        </div>
                        <input
                            type="range"
                            min={500}
                            max={30000}
                            step={500}
                            value={limits[b.id]}
                            onChange={e => setLimits(prev => ({ ...prev, [b.id]: Number(e.target.value) }))}
                            className="leak-range"
                        />
                    </div>
                ))}
            </div>

            <div className="leak-actions">
                <button type="button" className="sp-btn-primary" onClick={save}>
                    Save & go Home
                </button>
            </div>
        </div>
    );
}
